import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";
import { AdminHealthSnapshot } from "@/components/AdminHealthSnapshot";
import { AdminSyncIsPaidCard } from "@/components/AdminSyncIsPaidCard";
import { CashflowTimelineWidget } from "@/components/CashflowTimelineWidget";
import { Users, ShieldCheck, AlertTriangle, Coins, FileText, TrendingUp, Clock, Brain, ArrowUpCircle, Calendar as CalendarIcon, MapPin } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Printer, BellRing } from "lucide-react";
import { toast } from "sonner";
import { ExpiryNoticeDialog } from "@/components/ExpiryNoticeDialog";
import { StorageUsageWidget } from "@/components/StorageUsageWidget";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";

const TYPE_LABEL = {
  auto: "Auto", casa: "Casa", vita: "Vita", salute: "Salute",
  infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "RC",
};

const TOOLTIP_STYLE = { background: "#0A192F", border: "none", borderRadius: 6, color: "white" };
const BAR_RADIUS = [6, 6, 0, 0];

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const OnlineTimer = () => {
  const [startedAt] = useState(() => {
    const stored = parseInt(localStorage.getItem("session_started_at") || "", 10);
    if (!Number.isNaN(stored) && stored > 0) return stored;
    const now = Date.now();
    localStorage.setItem("session_started_at", String(now));
    return now;
  });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const startedDate = new Date(startedAt);
  const startedLabel = startedDate.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return (
    <div
      data-testid="online-timer"
      className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
      title={`Sessione iniziata alle ${startedLabel}`}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
      </span>
      <Clock className="h-3.5 w-3.5 text-emerald-700" />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-emerald-700">Online da</div>
        <div className="font-mono font-semibold text-emerald-900 tabular-nums text-sm" data-testid="online-elapsed">
          {formatElapsed(now - startedAt)}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, border, testid, to, onClick, filterHint }) => {
  const navigate = useNavigate();
  const clickable = !!(to || onClick);
  const handleClick = () => {
    if (onClick) return onClick();
    if (to) navigate(to);
  };
  const handleKeyDown = (e) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };
  return (
    <div
      data-testid={testid}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? (filterHint || "Apri elenco filtrato") : undefined}
      className={`bg-white border border-slate-200 ${border || "border-l-4 border-l-slate-400"} rounded-lg p-3 xl:p-5 transition-all duration-200 ${
        clickable
          ? "hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
          : "hover:shadow-md"
      }`}
    >
      <div className="flex items-center justify-between mb-2 xl:mb-3">
        <div className={`h-8 w-8 xl:h-9 xl:w-9 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4 xl:h-4.5 xl:w-4.5" />
        </div>
        {clickable && (
          <span className="text-[10px] text-slate-400 font-medium hidden xl:inline">Apri →</span>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{label}</p>
      <p
        className="font-display font-semibold mt-0.5 xl:mt-1 tabular-nums leading-tight break-words"
        style={{
          fontSize: "clamp(1rem, 2vw, 1.75rem)",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
        title={String(value)}
      >
        {value}
      </p>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  // 🆕 Feb 2026 — Polizze scadute fino a 30 giorni fa (per card comprimibile sotto quella imminente)
  const [expired, setExpired] = useState([]);
  const [autoIssues, setAutoIssues] = useState([]);  // polizze auto con revisione/bollo da gestire
  const [staleSends, setStaleSends] = useState(0);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const [resUsage, setResUsage] = useState(null);
  const [agendaStats, setAgendaStats] = useState(null);
  const [activeClosures, setActiveClosures] = useState([]);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [upgradeSent, setUpgradeSent] = useState(false);
  const [myEarnings, setMyEarnings] = useState(null);
  const [unpaidExpired, setUnpaidExpired] = useState(null);
  const [claimsSummary, setClaimsSummary] = useState(null); // 🆕 riepilogo sinistri
  const [claimStatusLabels, setClaimStatusLabels] = useState({}); // code → label
  // 🆕 Timestamp ultima ricarica + flag refreshing per feedback UI
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  // 🆕 Carica lookup una volta per etichette leggibili nel widget sinistri
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lookup-lists/claim_status");
        const map = {};
        (data || []).forEach((s) => { map[s.code] = s.label; });
        setClaimStatusLabels(map);
      } catch { /* silent */ }
    })();
  }, []);

  // 🆕 Loader unificato — chiama tutti i fetch della dashboard
  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [uRes, rRes, aRes, cRes, sRes, eRes, exRes, clRes] = await Promise.allSettled([
        api.get("/agency/ai-usage"),
        api.get("/agency/resource-usage"),
        api.get("/agenda/dashboard-stats"),
        api.get("/agency/closures/active"),
        api.get("/dashboard/stats"),
        api.get("/policies", { params: { expiring_days: 30 } }),
        api.get("/policies", { params: { expired_days: 30 } }),
        api.get("/claims/summary"),
      ]);
      if (uRes.status === "fulfilled") setAiUsage(uRes.value.data);
      if (rRes.status === "fulfilled") setResUsage(rRes.value.data);
      if (aRes.status === "fulfilled") setAgendaStats(aRes.value.data);
      if (cRes.status === "fulfilled") setActiveClosures(cRes.value.data || []);
      if (sRes.status === "fulfilled") setStats(sRes.value.data);
      if (eRes.status === "fulfilled") setExpiring(eRes.value.data || []);
      if (exRes.status === "fulfilled") setExpired(exRes.value.data || []);
      if (clRes.status === "fulfilled") setClaimsSummary(clRes.value.data);
      try { const ueu = await api.get("/dashboard/expired-unpaid-commissions"); setUnpaidExpired(ueu.data); } catch { /* silent */ }
      try {
        const allRes = await api.get("/policies");
        const allPol = allRes.data || [];
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const issues = [];
        for (const p of allPol) {
          if (p.type !== "auto") continue;
          const rev = p.next_review_date ? new Date(p.next_review_date) : null;
          if (rev) rev.setHours(0, 0, 0, 0);
          const revDays = rev ? Math.round((rev - today) / (1000 * 60 * 60 * 24)) : null;
          const b = p.bollo_due_date ? new Date(p.bollo_due_date) : null;
          if (b) b.setHours(0, 0, 0, 0);
          const bDays = b ? Math.round((b - today) / (1000 * 60 * 60 * 24)) : null;
          const flags = [];
          if (!rev) flags.push({ kind: "rev-missing", color: "amber", label: "Revisione mancante" });
          else if (revDays < 0) flags.push({ kind: "rev-expired", color: "rose", label: `Revisione scaduta da ${Math.abs(revDays)}gg` });
          else if (revDays <= 30) flags.push({ kind: "rev-soon", color: "orange", label: `Revisione tra ${revDays}gg` });
          if (b && bDays < 0) flags.push({ kind: "bollo-expired", color: "rose", label: `Bollo scaduto da ${Math.abs(bDays)}gg` });
          else if (b && bDays <= 30) flags.push({ kind: "bollo-soon", color: "orange", label: `Bollo tra ${bDays}gg` });
          if (flags.length) issues.push({ ...p, flags });
        }
        const sevOrder = { "rev-expired": 0, "bollo-expired": 1, "rev-missing": 2, "rev-soon": 3, "bollo-soon": 4 };
        issues.sort((a, b2) => {
          const sa = Math.min(...a.flags.map((f) => sevOrder[f.kind] ?? 99));
          const sb = Math.min(...b2.flags.map((f) => sevOrder[f.kind] ?? 99));
          return sa - sb;
        });
        setAutoIssues(issues);
      } catch { /* ignore */ }
      try {
        const sum = await api.get("/quotes/sends-summary");
        const total = Object.values(sum.data || {}).reduce((acc, v) => acc + (v.stale_replies || 0), 0);
        setStaleSends(total);
      } catch { /* ignore */ }
      setLastRefresh(new Date());
    } catch (e) {
      logger.error(e);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== "collaboratore") return;
    (async () => {
      try {
        const { data } = await api.get("/commissions/my-earnings");
        setMyEarnings(data);
      } catch { /* silent */ }
    })();
  }, [user?.role]);

  const requestPlanUpgrade = async () => {
    if (upgradeBusy) return;
    const note = window.prompt(
      "Inviamo una richiesta di upgrade del piano al gestore della piattaforma.\n\nAggiungi una nota (opzionale, es. di quanti clienti/polizze hai bisogno):",
      ""
    );
    if (note === null) return; // annullato
    setUpgradeBusy(true);
    try {
      await api.post("/agency/request-plan-upgrade", { note });
      setUpgradeSent(true);
      toast.success("Richiesta inviata. Sarai contattato a breve.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio della richiesta");
    } finally {
      setUpgradeBusy(false);
    }
  };

  const sendReminders = async () => {
    if (!window.confirm("Inviare email promemoria a tutti i clienti con polizza in scadenza nei prossimi 30 giorni?")) return;
    setSendingReminders(true);
    try {
      const { data } = await api.post("/notifications/send-expiring-reminders");
      toast.success(`Promemoria inviati: ${data.client_emails_queued} clienti + ${data.staff_emails_queued} agenti (${data.expiring} polizze)`);
    } catch (e) { toast.error("Errore invio promemoria"); }
    finally { setSendingReminders(false); }
  };

  // 🆕 09/07/2026 — Invia avviso di scadenza a un singolo cliente dalla card Dashboard
  // Con anteprima (opzione "Non chiedermelo più") + invio via email E WhatsApp + tracciamento.
  const [sendingExpiryId, setSendingExpiryId] = useState(null);
  const [expiryPreview, setExpiryPreview] = useState(null); // { policy }
  // 🆕 28/07/2026 — Dialog "Avvisa" con scelta canale Email / WhatsApp / Entrambi
  const [noticeDialog, setNoticeDialog] = useState(null); // { policy, client }

  const openExpiryNotice = useCallback(async (policy) => {
    if (!policy?.id) return;
    setSendingExpiryId(policy.id);
    try {
      let client = null;
      if (policy.client_id) {
        try {
          const { data } = await api.get(`/clients/${policy.client_id}`);
          // 🆕 Feb 2026 — L'endpoint /clients/{id} restituisce { client, policies }
          // Precedentemente veniva usato `data` direttamente e mail/mobile risultavano
          // sempre "non disponibile" nel dialog "Avvisa scadenza".
          client = data?.client || data;
        } catch { /* ignora */ }
      }
      setNoticeDialog({ policy, client });
    } finally {
      setSendingExpiryId(null);
    }
  }, []);


  const performExpirySend = useCallback(async (policy) => {
    if (!policy?.id) return;
    setSendingExpiryId(policy.id);
    try {
      const { data } = await api.post(`/policies/${policy.id}/send-expiry-notice`);
      // Feedback email
      if (data?.email_sent) {
        toast.success(`Email inviata a ${data.target_email || policy.client_name}`);
      } else if (data?.target_email) {
        toast.warning(`Email non inviata a ${data.target_email} (verifica SMTP)`);
      } else {
        toast.info("Cliente senza email — verrà usato solo WhatsApp");
      }
      // Apri WhatsApp se disponibile (invio duplice)
      if (data?.wa_url || data?.whatsapp_url) {
        window.open(data.wa_url || data.whatsapp_url, "_blank", "noopener,noreferrer");
        toast.success("WhatsApp aperto — completa l'invio dalla web app");
      } else {
        toast.warning("Cliente senza telefono — WhatsApp non disponibile");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio dell'avviso");
    } finally {
      setSendingExpiryId(null);
    }
  }, []);

  const sendExpiryToClient = useCallback((policy) => {
    if (!policy?.id) return;
    // Se l'utente ha disattivato l'anteprima → invia direttamente
    if (shouldSkipEmailPreview("policy_expiry_notice")) {
      performExpirySend(policy);
      return;
    }
    // Altrimenti apri il dialog di anteprima
    setExpiryPreview({ policy });
  }, [performExpirySend]);

  // 🆕 Ricarica iniziale + auto-refresh su focus/visibilitychange
  useEffect(() => {
    loadAll();
    const onVis = () => { if (!document.hidden) loadAll(); };
    const onFocus = () => loadAll();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    // Polling di fallback ogni 30s per aggiornare i totali senza intervento utente
    const iv = setInterval(loadAll, 30000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      clearInterval(iv);
    };
  }, [loadAll]);

  const chartData = (stats?.by_type || []).map((b) => ({
    name: TYPE_LABEL[b.type] || b.type,
    Premi: Math.round(b.premium),
  }));

  return (
    <div className="space-y-4 xl:space-y-8 max-w-7xl mx-auto animate-fade-up" data-testid="dashboard-page">
      <header className="flex items-end justify-between flex-wrap gap-3 xl:gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-1 xl:mb-2">Cruscotto</p>
          <h1 className="font-display text-2xl xl:text-3xl 2xl:text-4xl font-semibold tracking-tight">
            Ciao, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-xs xl:text-sm text-slate-500 mt-0.5 xl:mt-1">Ecco la situazione dell&apos;agenzia oggi.</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <Button
            variant="outline"
            data-testid="refresh-dashboard-btn"
            onClick={loadAll}
            disabled={refreshing}
            className="text-xs sm:text-sm"
            title={`Ultimo aggiornamento: ${lastRefresh.toLocaleTimeString("it-IT")}`}
          >
            <span className={refreshing ? "inline-block animate-spin" : "inline-block"}>🔄</span>
            <span className="ml-1.5">{refreshing ? "Aggiorno…" : "Aggiorna"}</span>
            <span className="hidden sm:inline text-slate-400 ml-2">· {lastRefresh.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
          </Button>
          <Button data-testid="send-reminders-btn" onClick={sendReminders} disabled={sendingReminders} className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm">
            <BellRing className="h-4 w-4 mr-1.5" /> {sendingReminders ? "Invio…" : <><span className="hidden sm:inline">Promemoria </span>scadenze</>}
          </Button>
          <Button variant="outline" data-testid="print-dashboard" onClick={() => window.print()} className="text-xs sm:text-sm"><Printer className="h-4 w-4 mr-1.5" /> Stampa</Button>
          <OnlineTimer />
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-slate-500">Data</p>
            <p className="font-display text-lg">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</p>
          </div>
        </div>
      </header>

      {/* Widget Compensi Collaboratore (visibile solo per role=collaboratore) */}
      {user?.role === "collaboratore" && myEarnings && (
        <div data-testid="collab-earnings-widget" className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border border-emerald-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">I tuoi compensi · {myEarnings.month}</p>
              <h2 className="font-display text-2xl font-semibold text-emerald-900 mt-1">Ciao {myEarnings.user.name}</h2>
              <p className="text-xs text-slate-600 mt-0.5">Provvigione: <strong>{myEarnings.user.commission_share_percent}%</strong> · Extra: <strong>{myEarnings.user.extra_share_percent}%</strong></p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="bg-white rounded-lg px-4 py-3 border border-emerald-200 text-center min-w-[120px]">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Maturate (mese)</p>
                <p className="font-display text-2xl font-bold text-emerald-700 tabular-nums" data-testid="collab-maturated">{formatCurrency(myEarnings.totals.maturated_in_month)}</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-3 border border-blue-200 text-center min-w-[120px]">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Incassate</p>
                <p className="font-display text-2xl font-bold text-blue-700 tabular-nums" data-testid="collab-collected">{formatCurrency(myEarnings.totals.collected_in_month)}</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-3 border border-amber-200 text-center min-w-[120px]">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Da incassare</p>
                <p className="font-display text-2xl font-bold text-amber-700 tabular-nums" data-testid="collab-pending">{formatCurrency(myEarnings.totals.pending_in_month)}</p>
              </div>
            </div>
          </div>
          {myEarnings.policies?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-700">
                  <tr>
                    <th className="text-left px-3 py-2">Polizza</th>
                    <th className="text-left px-3 py-2">Ramo</th>
                    <th className="text-left px-3 py-2">Decorrenza</th>
                    <th className="text-right px-3 py-2">Premio</th>
                    <th className="text-right px-3 py-2">Mia provv.</th>
                    <th className="text-right px-3 py-2">Mio extra</th>
                    <th className="text-right px-3 py-2">Totale</th>
                    <th className="text-center px-3 py-2">Pagata?</th>
                  </tr>
                </thead>
                <tbody data-testid="collab-policies-table">
                  {myEarnings.policies.slice(0, 10).map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{p.policy_number || p.id.slice(0, 8)}</td>
                      <td className="px-3 py-2"><PolicyTypeBadge code={p.type} size="xs" testid={`dash-myearn-type-${p.id}`} /></td>
                      <td className="px-3 py-2 text-xs">{p.start_date ? formatDate(p.start_date) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(p.premium)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatCurrency(p.my_commission)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">{formatCurrency(p.my_extra)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(p.my_total)}</td>
                      <td className="px-3 py-2 text-center">{p.is_paid ? "✅" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 xl:gap-4">
        <StatCard icon={Users} label="Clienti" value={stats?.clients ?? "—"} accent="bg-blue-100 text-blue-700" border="border-l-4 border-l-blue-500" testid="stat-clients" to="/clients" filterHint="Vai all'elenco clienti" />
        <StatCard icon={ShieldCheck} label="Polizze attive" value={stats?.policies_active ?? "—"} accent="bg-amber-100 text-amber-700" border="border-l-4 border-l-amber-500" testid="stat-active" to="/policies?filter=attiva" filterHint="Mostra solo polizze attive" />
        <StatCard icon={AlertTriangle} label="In scadenza 30gg" value={stats?.policies_expiring_30 ?? "—"} accent="bg-orange-100 text-orange-700" border="border-l-4 border-l-orange-500" testid="stat-expiring" to="/policies?filter=in_scadenza" filterHint="Mostra polizze in scadenza entro 30 giorni" />
        <StatCard icon={Coins} label="Premi totali" value={formatCurrency(stats?.total_premium)} accent="bg-emerald-100 text-emerald-700" border="border-l-4 border-l-emerald-500" testid="stat-premium" to="/policies" filterHint="Vai a tutte le polizze" />
        <StatCard icon={FileText} label="Preventivi aperti" value={stats?.quotes_open ?? "—"} accent="bg-emerald-100 text-emerald-700" border="border-l-4 border-l-emerald-500" testid="stat-quotes" to="/quotes?filter=open" filterHint="Mostra preventivi aperti (nuovi + in lavorazione)" />
        <StatCard icon={TrendingUp} label="Scadute" value={stats?.policies_expired ?? "—"} accent="bg-red-100 text-red-700" border="border-l-4 border-l-red-500" testid="stat-expired" to="/policies?filter=scaduta" filterHint="Mostra solo polizze scadute" />
      </div>

      {/* 🆕 Widget spazio archiviazione (compatto — badge riga singola) + Agenda subito sotto */}
      <div className="flex items-center gap-3 flex-wrap">
        <StorageUsageWidget compact />
      </div>

      {/* 📅 Widget Agenda di oggi — spostato subito sotto lo spazio archiviazione */}
      {agendaStats && (
        <a href="/agenda" className="block rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 hover:shadow-md transition-shadow" data-testid="agenda-widget">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-blue-100 text-blue-700 shrink-0">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <p className="text-sm font-semibold text-blue-900">Agenda</p>
                <div className="flex gap-3 text-xs">
                  <span className="text-blue-800"><b className="text-lg">{agendaStats.today_count}</b> oggi</span>
                  <span className="text-slate-600"><b>{agendaStats.tomorrow_count}</b> domani</span>
                  <span className="text-slate-500"><b>{agendaStats.week_count}</b> 7gg</span>
                </div>
              </div>
              {agendaStats.upcoming?.length > 0 ? (
                <div className="space-y-1">
                  {agendaStats.upcoming.slice(0, 3).map((ev) => {
                    const d = new Date(ev.start_at);
                    const isToday = d.toDateString() === new Date().toDateString();
                    return (
                      <div key={ev.id} className="text-xs flex items-center gap-2 py-0.5">
                        <span className="font-mono tabular-nums text-slate-600 w-24 shrink-0">
                          {isToday ? "Oggi " : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) + " "}
                          {ev.all_day ? "" : d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <span className="text-slate-800 truncate">{ev.title}</span>
                        {ev.location && <span className="text-slate-400 hidden sm:flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{ev.location}</span>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-500">Nessun evento in programma — clicca per aggiungerne uno.</p>
              )}
            </div>
          </div>
        </a>
      )}

      {/* 🔔 Scadenze imminenti — riquadro d'apertura collassabile (aperto di default se ci sono polizze) */}
      <details
        className="rounded-xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-5 shadow-sm group"
        data-testid="expiring-highlight"
        open={expiring.length > 0}
      >
        <summary className="cursor-pointer list-none flex items-center justify-between flex-wrap gap-3 -m-1 p-1 rounded-md hover:bg-orange-100/40">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-orange-500 text-white flex items-center justify-center shadow">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-orange-700 font-semibold">Prossimi 30 giorni</p>
              <h3 className="font-display text-2xl font-bold text-orange-900 flex items-center gap-2">
                Scadenze imminenti
                <span className="text-orange-600 text-xs font-normal opacity-70 group-open:hidden">▸ apri</span>
                <span className="text-orange-600 text-xs font-normal opacity-70 hidden group-open:inline">▾ chiudi</span>
              </h3>
              <p className="text-xs text-slate-600 mt-0.5">
                <strong className="tabular-nums text-orange-800">{expiring.length}</strong> polizz{expiring.length === 1 ? "a" : "e"} in scadenza — agisci prima che scadano
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/policies?filter=expiring"); }}
            className="border-orange-300 text-orange-800 hover:bg-orange-100"
            data-testid="expiring-highlight-see-all"
          >
            Vedi tutte
          </Button>
        </summary>
        <div className="mt-3">
        {expiring.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nessuna polizza in scadenza nei prossimi 30 giorni. Ottimo!</p>
        ) : (
          <div className="bg-white border border-orange-200 rounded-lg overflow-hidden" data-testid="expiring-list">
            <table className="w-full text-sm">
              <thead className="bg-orange-50/60 text-[11px] uppercase tracking-wide text-orange-700">
                <tr>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-left px-3 py-2">Polizza</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Scadenza</th>
                  <th className="text-left px-3 py-2">Stato</th>
                  <th className="text-right px-3 py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {expiring.slice(0, 8).map((p) => (
                  <tr
                    key={p.id}
                    className="border-t border-slate-100 hover:bg-orange-50/40"
                  >
                    <td className="px-3 py-2 text-sm font-medium cursor-pointer" onDoubleClick={() => navigate(`/policies?focus=${p.id}`)} title="Doppio click per aprire la polizza">{p.client_name || "Cliente"}</td>
                    <td className="px-3 py-2 text-xs font-mono">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span>{p.policy_number || "—"}</span>
                        {/* 🆕 16/07/2026 — Marca + modello + targa accanto al n° polizza */}
                        {(p.vehicle_brand || p.vehicle_model) && (
                          <span
                            className="font-sans text-[11px] font-medium text-slate-700 normal-case"
                            data-testid={`dash-expiring-vehicle-${p.id}`}
                            title="Veicolo assicurato"
                          >
                            {[p.vehicle_brand, p.vehicle_model].filter(Boolean).join(" ")}
                          </span>
                        )}
                        {p.vehicle_plate && (
                          <span
                            className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-700"
                            data-testid={`dash-expiring-plate-${p.id}`}
                            title="Targa"
                          >
                            {p.type === "moto" ? "🏍️" : (p.type === "auto" ? "🚗" : "🚙")}
                            {String(p.vehicle_plate).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs"><PolicyTypeBadge code={p.type} size="xs" testid={`dash-expiring-type-${p.id}`} /></td>
                    <td className="px-3 py-2 text-sm font-medium">
                      {formatDate(p.effective_end_date || p.end_date)}
                      {p.effective_end_date && p.end_date && p.effective_end_date !== p.end_date && (
                        <span className="ml-1 text-[8px] font-bold px-1 py-px rounded bg-blue-100 text-blue-800 border border-blue-200 uppercase tracking-tight align-middle" title={`Prossima rata da pagare (fine polizza: ${formatDate(p.end_date)})`}>rata</span>
                      )}
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); openExpiryNotice(p); }}
                        disabled={sendingExpiryId === p.id}
                        className="h-7 px-2 text-[11px] border-orange-300 text-orange-800 hover:bg-orange-100"
                        data-testid={`dash-expiring-notify-${p.id}`}
                        title="Invia avviso di scadenza al cliente via email"
                      >
                        <BellRing className="h-3 w-3 mr-1" />
                        {sendingExpiryId === p.id ? "Invio…" : "Avvisa"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </details>

      {/* 🆕 Feb 2026 — Scadute negli ultimi 30 giorni (chiuso di default; non allarma se vuoto) */}
      <details
        className="rounded-xl border-2 border-rose-300 bg-gradient-to-br from-rose-50 via-white to-red-50 p-5 shadow-sm group"
        data-testid="expired-highlight"
      >
        <summary className="cursor-pointer list-none flex items-center justify-between flex-wrap gap-3 -m-1 p-1 rounded-md hover:bg-rose-100/40">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-rose-600 text-white flex items-center justify-center shadow">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-rose-700 font-semibold">Ultimi 30 giorni</p>
              <h3 className="font-display text-2xl font-bold text-rose-900 flex items-center gap-2">
                Scadute da recuperare
                <span className="text-rose-600 text-xs font-normal opacity-70 group-open:hidden">▸ apri</span>
                <span className="text-rose-600 text-xs font-normal opacity-70 hidden group-open:inline">▾ chiudi</span>
              </h3>
              <p className="text-xs text-slate-600 mt-0.5">
                <strong className="tabular-nums text-rose-800">{expired.length}</strong> polizz{expired.length === 1 ? "a scaduta" : "e scadute"} — contatta il cliente per il rinnovo
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/policies?filter=expired"); }}
            className="border-rose-300 text-rose-800 hover:bg-rose-100"
            data-testid="expired-highlight-see-all"
          >
            Vedi tutte
          </Button>
        </summary>
        <div className="mt-3">
        {expired.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nessuna polizza scaduta negli ultimi 30 giorni. Ottimo lavoro!</p>
        ) : (
          <div className="bg-white border border-rose-200 rounded-lg overflow-hidden" data-testid="expired-list">
            <table className="w-full text-sm">
              <thead className="bg-rose-50/60 text-[11px] uppercase tracking-wide text-rose-700">
                <tr>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-left px-3 py-2">Polizza</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Scaduta il</th>
                  <th className="text-left px-3 py-2">Giorni fa</th>
                  <th className="text-right px-3 py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {expired.slice(0, 8).map((p) => {
                  const endStr = p.effective_end_date || p.end_date;
                  let daysAgo = null;
                  if (endStr) {
                    try {
                      const d = new Date(endStr); d.setHours(0, 0, 0, 0);
                      const today = new Date(); today.setHours(0, 0, 0, 0);
                      daysAgo = Math.max(0, Math.round((today - d) / (1000 * 60 * 60 * 24)));
                    } catch { daysAgo = null; }
                  }
                  return (
                  <tr
                    key={p.id}
                    className="border-t border-slate-100 hover:bg-rose-50/40"
                  >
                    <td className="px-3 py-2 text-sm font-medium cursor-pointer" onDoubleClick={() => navigate(`/policies?focus=${p.id}`)} title="Doppio click per aprire la polizza">{p.client_name || "Cliente"}</td>
                    <td className="px-3 py-2 text-xs font-mono">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span>{p.policy_number || "—"}</span>
                        {(p.vehicle_brand || p.vehicle_model) && (
                          <span
                            className="font-sans text-[11px] font-medium text-slate-700 normal-case"
                            data-testid={`dash-expired-vehicle-${p.id}`}
                            title="Veicolo assicurato"
                          >
                            {[p.vehicle_brand, p.vehicle_model].filter(Boolean).join(" ")}
                          </span>
                        )}
                        {p.vehicle_plate && (
                          <span
                            className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-700"
                            data-testid={`dash-expired-plate-${p.id}`}
                            title="Targa"
                          >
                            {p.type === "moto" ? "🏍️" : (p.type === "auto" ? "🚗" : "🚙")}
                            {String(p.vehicle_plate).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs"><PolicyTypeBadge code={p.type} size="xs" testid={`dash-expired-type-${p.id}`} /></td>
                    <td className="px-3 py-2 text-sm font-medium text-rose-800">{formatDate(endStr)}</td>
                    <td className="px-3 py-2 text-xs">
                      {daysAgo !== null && (
                        <span className="inline-flex items-center rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 text-[11px] font-semibold border border-rose-200">
                          {daysAgo === 0 ? "oggi" : `${daysAgo} g fa`}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); openExpiryNotice(p); }}
                        disabled={sendingExpiryId === p.id}
                        className="h-7 px-2 text-[11px] border-rose-300 text-rose-800 hover:bg-rose-100"
                        data-testid={`dash-expired-notify-${p.id}`}
                        title="Contatta il cliente per il rinnovo"
                      >
                        <BellRing className="h-3 w-3 mr-1" />
                        {sendingExpiryId === p.id ? "Invio…" : "Contatta"}
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </details>

      {/* 🆕 Widget riepilogo Sinistri (collassabile, chiuso di default) */}
      {claimsSummary && (claimsSummary.total > 0 || claimsSummary.open > 0) && (
        <details
          data-testid="claims-summary-widget"
          className="bg-gradient-to-r from-amber-50 via-orange-50 to-white border border-amber-200 rounded-xl p-4 group"
        >
          <summary className="cursor-pointer list-none flex items-center justify-between flex-wrap gap-3 -m-1 p-1 rounded-md hover:bg-amber-100/40">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-amber-600 text-white flex items-center justify-center">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">Post-vendita · Sinistri</p>
                <h3 className="font-display text-base font-semibold text-amber-900">Sinistri gestiti</h3>
                <p className="text-xs text-slate-600 mt-0.5">
                  <strong className="tabular-nums">{claimsSummary.total}</strong> totali · <strong className="text-amber-800 tabular-nums">{claimsSummary.open}</strong> aperti · <strong className="text-emerald-800 tabular-nums">{claimsSummary.closed}</strong> chiusi
                  {claimsSummary.estimated_total > 0 && <> · importo stimato <strong className="text-slate-800 tabular-nums">{formatCurrency(claimsSummary.estimated_total)}</strong></>}
                </p>
              </div>
            </div>
            <span className="text-xs text-amber-700 font-medium select-none">Espandi ▾</span>
          </summary>
          <div className="mt-3">
            {(claimsSummary.recent || []).length > 0 && (
              <div className="bg-white border border-amber-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50/60 text-[11px] uppercase tracking-wide text-amber-700">
                    <tr>
                      <th className="text-left px-3 py-2">Data evento</th>
                      <th className="text-left px-3 py-2">Cliente</th>
                      <th className="text-left px-3 py-2">Polizza</th>
                      <th className="text-left px-3 py-2">Descrizione</th>
                      <th className="text-left px-3 py-2">Stato</th>
                      <th className="text-right px-3 py-2">Importo stimato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claimsSummary.recent.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-slate-100 hover:bg-amber-50/40 cursor-pointer"
                        onDoubleClick={() => navigate(`/policies?focus=${c.policy_id}`)}
                        title="Doppio click per aprire la polizza"
                        data-testid={`claim-recent-${c.id}`}
                      >
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{c.event_date ? formatDate(c.event_date) : "—"}</td>
                        <td className="px-3 py-2 text-xs">{c.client_name || "—"}</td>
                        <td className="px-3 py-2 text-xs font-mono">{c.policy_number || c.policy_id?.slice(0, 8)}</td>
                        <td className="px-3 py-2 text-xs text-slate-700 max-w-[280px] truncate" title={c.description}>{c.description}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className="inline-block px-2 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800">
                            {claimStatusLabels[c.status] || c.status || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-right tabular-nums font-semibold text-slate-800">
                          {c.estimated_amount != null ? formatCurrency(c.estimated_amount) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                data-testid="claims-summary-see-all"
                onClick={() => navigate("/policies")}
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                Gestisci dalle polizze
              </Button>
            </div>
          </div>
        </details>
      )}

      {/* Widget: Polizze scadute con provvigione non incassata (collassabile, chiuso di default) */}
      {unpaidExpired && unpaidExpired.count > 0 && (
        <details
          data-testid="unpaid-expired-widget"
          className="bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50 border border-rose-200 rounded-xl p-4"
        >
          <summary className="cursor-pointer list-none flex items-center justify-between flex-wrap gap-3 -m-1 p-1 rounded-md hover:bg-rose-100/40">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-rose-600 text-white flex items-center justify-center">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-rose-700 font-semibold">Cash-flow · provvigioni arretrate</p>
                <h3 className="font-display text-base font-semibold text-rose-900">Polizze scadute non incassate</h3>
                <p className="text-xs text-slate-600 mt-0.5">
                  <strong className="tabular-nums">{unpaidExpired.count}</strong> polizz{unpaidExpired.count === 1 ? "a" : "e"} · pending totale <strong className="text-rose-800 tabular-nums">{formatCurrency(unpaidExpired.total_pool_pending)}</strong>
                  {unpaidExpired.total_extra > 0 && <> · extra <strong className="tabular-nums">{formatCurrency(unpaidExpired.total_extra)}</strong></>}
                </p>
              </div>
            </div>
            <span className="text-xs text-rose-700 font-medium select-none">Espandi ▾</span>
          </summary>
          <div className="mt-3">
            <div className="bg-white border border-rose-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-rose-50/60 text-[11px] uppercase tracking-wide text-rose-700">
                  <tr>
                    <th className="text-left px-3 py-2">Polizza</th>
                    <th className="text-left px-3 py-2">Cliente</th>
                    <th className="text-left px-3 py-2">Compagnia</th>
                    <th className="text-left px-3 py-2">Scadenza</th>
                    <th className="text-right px-3 py-2">Da incassare</th>
                  </tr>
                </thead>
                <tbody>
                  {unpaidExpired.policies.slice(0, 8).map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-slate-100 hover:bg-rose-50/40 cursor-pointer"
                      onClick={() => navigate(`/policies?focus=${p.id}`)}
                      title="Apri polizza"
                      data-testid={`unpaid-row-${p.id}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{p.policy_number || p.id.slice(0, 8)}</td>
                      <td className="px-3 py-2">{p.client_name}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{p.company_name}</td>
                      <td className="px-3 py-2 text-xs">
                        {p.end_date ? formatDate(p.end_date) : "—"}
                        <span className="ml-1.5 text-[10px] text-rose-600 font-semibold">·{p.days_overdue}gg</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-800">{formatCurrency(p.pool_pending)}</td>
                    </tr>
                  ))}
                  {unpaidExpired.policies.length > 8 && (
                    <tr className="bg-slate-50">
                      <td colSpan="5" className="px-3 py-2 text-center text-xs text-slate-500">
                        + altre {unpaidExpired.policies.length - 8} polizze · clicca &quot;Vedi tutte&quot;
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-[10px] text-slate-500 italic">Clicca su una riga per aprire la polizza e registrare l&apos;incasso.</p>
              <Button
                variant="outline"
                size="sm"
                data-testid="unpaid-expired-see-all"
                onClick={() => navigate("/policies?status=scaduta")}
                className="border-rose-300 text-rose-800 hover:bg-rose-100"
              >
                Vedi tutte
              </Button>
            </div>
          </div>
        </details>
      )}

      {/* Tasso di conversione preventivi → polizze (ultimi 30 giorni) */}
      {stats?.quotes_recent_total_30d != null && stats.quotes_recent_total_30d > 0 && (
        <div
          data-testid="conversion-rate-widget"
          className="bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 border border-indigo-200 rounded-lg p-5"
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-md bg-indigo-600 text-white flex items-center justify-center">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-indigo-700 font-semibold">Tasso conversione preventivi · ultimi 30gg</p>
                <p className="font-display text-3xl font-bold tabular-nums text-indigo-900 mt-0.5" data-testid="conversion-rate-value">
                  {stats.conversion_rate_30d}%
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  <strong className="tabular-nums">{stats.quotes_recent_converted_30d}</strong> polizze da <strong className="tabular-nums">{stats.quotes_recent_total_30d}</strong> preventivi creati
                </p>
              </div>
            </div>
            <div className="flex-1 max-w-md">
              <div className="h-2.5 bg-white/70 rounded-full overflow-hidden border border-indigo-100">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500 transition-all"
                  style={{ width: `${Math.min(100, Math.max(0, stats.conversion_rate_30d))}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-slate-500 mt-1.5">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Widget consumo IA giornaliero (X/limite) */}
      {aiUsage && !aiUsage.unlimited && (
        (() => {
          const used = aiUsage.used?.daily ?? 0;
          const limit = aiUsage.limits?.daily ?? 0;
          const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
          const tone = limit > 0 && used >= limit
            ? { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", bar: "bg-rose-500" }
            : pct >= 80
              ? { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", bar: "bg-amber-500" }
              : { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-800", bar: "bg-violet-500" };
          return (
            <div
              data-testid="ai-usage-widget"
              className={`rounded-lg border ${tone.border} ${tone.bg} p-3 flex items-center gap-3`}
            >
              <div className={`h-9 w-9 rounded-md flex items-center justify-center bg-white/60 ${tone.text}`}>
                <Brain className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <p className={`text-xs font-semibold ${tone.text}`}>Consumo IA (OCR, riepiloghi, estrazioni)</p>
                  <p className={`text-xs tabular-nums ${tone.text}`} data-testid="ai-usage-counter">
                    Oggi <b>{used}</b>{limit > 0 ? `/${limit}` : " (illimitato)"}
                    {" · "}Mese <b>{aiUsage.used?.monthly ?? 0}</b>
                    {aiUsage.limits?.monthly > 0 ? `/${aiUsage.limits.monthly}` : ""}
                  </p>
                </div>
                {limit > 0 && (
                  <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mt-1.5">
                    <div className={`${tone.bar} h-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                )}
                {limit > 0 && used >= limit && (
                  <p className="text-[11px] mt-1 text-rose-700">
                    Limite giornaliero raggiunto — riprova domani o contatta il supporto per aumentarlo.
                  </p>
                )}
              </div>
            </div>
          );
        })()
      )}

      {/* Widget consumo risorse: clienti e polizze (mostrato solo se sono impostati dei limiti) */}
      {resUsage && !resUsage.unlimited && (resUsage.limits?.max_clients || resUsage.limits?.max_policies) && (
        (() => {
          const items = [];
          if (resUsage.limits?.max_clients) {
            const u = resUsage.used?.clients ?? 0;
            const l = resUsage.limits.max_clients;
            const pct = Math.min(100, Math.round((u / l) * 100));
            items.push({ label: "Clienti", used: u, limit: l, pct, icon: Users });
          }
          if (resUsage.limits?.max_policies) {
            const u = resUsage.used?.policies ?? 0;
            const l = resUsage.limits.max_policies;
            const pct = Math.min(100, Math.round((u / l) * 100));
            items.push({ label: "Polizze", used: u, limit: l, pct, icon: ShieldCheck });
          }
          const worstPct = Math.max(...items.map((i) => i.pct), 0);
          const tone = worstPct >= 100
            ? { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", bar: "bg-rose-500" }
            : worstPct >= 80
              ? { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", bar: "bg-amber-500" }
              : { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-800", bar: "bg-indigo-500" };
          return (
            <div
              data-testid="resource-usage-widget"
              className={`rounded-lg border ${tone.border} ${tone.bg} p-3`}
            >
              <p className={`text-xs font-semibold ${tone.text} mb-2`}>Utilizzo del piano</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <div key={it.label} className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-md flex items-center justify-center bg-white/60 ${tone.text}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className={`text-xs font-medium ${tone.text}`}>{it.label}</p>
                          <p className={`text-xs tabular-nums ${tone.text}`} data-testid={`res-usage-${it.label.toLowerCase()}`}>
                            <b>{it.used}</b>/{it.limit}
                          </p>
                        </div>
                        <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mt-1.5">
                          <div className={`${tone.bar} h-full transition-all`} style={{ width: `${it.pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {worstPct >= 100 && (
                <p className="text-[11px] mt-2 text-rose-700">
                  Limite raggiunto — contatta l&apos;amministratore o passa a un piano superiore per continuare.
                </p>
              )}
              {worstPct >= 80 && (user?.role === "admin") && (
                <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 rounded-md bg-white/70 border border-white px-2.5 py-2">
                  <p className={`text-[11px] ${tone.text}`}>
                    {worstPct >= 100
                      ? "Hai raggiunto il limite del piano: l'upgrade ti permette di tornare a inserire nuovi dati."
                      : `Sei al ${worstPct}% del piano. Considera un upgrade per evitare blocchi.`}
                  </p>
                  <Button
                    size="sm"
                    onClick={requestPlanUpgrade}
                    disabled={upgradeBusy || upgradeSent}
                    data-testid="request-plan-upgrade-btn"
                    className={`h-7 text-[11px] ${worstPct >= 100 ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"} text-white`}
                  >
                    <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />
                    {upgradeSent ? "Richiesta inviata" : upgradeBusy ? "Invio…" : "Aggiorna piano"}
                  </Button>
                </div>
              )}
            </div>
          );
        })()
      )}

      {/* Banner chiusure attive (staff) */}
      {activeClosures.length > 0 && (
        <div className="space-y-2" data-testid="closure-banner">
          {activeClosures.map((cl) => (
            <div key={cl.id} className="rounded-lg border border-rose-300 bg-gradient-to-r from-rose-50 to-amber-50 p-3 flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                <span className="text-lg">{cl.kind === "summer" ? "🌴" : cl.kind === "holiday" ? "🎄" : "🔒"}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <p className="text-sm font-semibold text-rose-900">{cl.title}</p>
                  <p className="text-[11px] font-mono tabular-nums text-rose-700 bg-white/70 px-2 py-0.5 rounded">
                    {new Date(cl.start_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                    {" → "}
                    {new Date(cl.end_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                  </p>
                </div>
                {cl.message && <p className="text-xs text-rose-800/80 mt-0.5">{cl.message}</p>}
                {cl.hours && <p className="text-xs text-amber-800 mt-0.5">⏱ {cl.hours}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Banner: invii senza risposta da oltre 5 giorni (collassabile, chiuso di default) */}
      {staleSends > 0 && (
        <details
          data-testid="stale-sends-widget"
          className="rounded-lg border border-rose-200 bg-gradient-to-r from-rose-50 to-amber-50 p-3"
        >
          <summary className="cursor-pointer list-none flex items-center justify-between flex-wrap gap-3 -m-1 p-1 rounded-md hover:bg-rose-100/40">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-rose-600 text-white flex items-center justify-center">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-rose-700 font-semibold">Preventivi partner</p>
                <h3 className="font-display text-base font-semibold text-rose-900">Invii a partner senza risposta</h3>
                <p className="text-xs text-slate-600 mt-0.5">
                  <strong className="tabular-nums text-rose-800">{staleSends}</strong> invio{staleSends === 1 ? "" : "i"} senza risposta da oltre 5 giorni
                </p>
              </div>
            </div>
            <span className="text-xs text-rose-700 font-medium select-none">Espandi ▾</span>
          </summary>
          <div className="mt-3 text-xs text-rose-800 space-y-2">
            <p>Apri i preventivi con badge rosso 📤 e usa il pulsante &quot;🔁 Reinvia&quot; o solleciti direttamente il broker/compagnia.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/quotes")}
              className="border-rose-300 text-rose-800 hover:bg-rose-100"
              data-testid="stale-sends-see-all"
            >
              Vai ai preventivi →
            </Button>
          </div>
        </details>
      )}

      {/* 🆕 22/07/2026 — Cash-flow provvigionale timeline */}
      <CashflowTimelineWidget />

      {user?.role === "admin" && <AdminHealthSnapshot />}
      {user?.role === "admin" && <AdminSyncIsPaidCard />}

      <div className="grid lg:grid-cols-3 gap-4 xl:gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-4 xl:p-6">
          <div className="flex items-center justify-between mb-4 xl:mb-6">
            <div>
              <h2 className="font-display text-lg xl:text-xl font-semibold">Premi per tipologia</h2>
              <p className="text-xs xl:text-sm text-slate-500">Distribuzione del portafoglio in EUR.</p>
            </div>
          </div>
          <div className="h-56 xl:h-72" data-testid="dashboard-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v) => formatCurrency(v)}
                />
                <Bar dataKey="Premi" fill="#2563EB" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 xl:p-6">
          <h2 className="font-display text-lg xl:text-xl font-semibold mb-1">Scadenze imminenti</h2>
          <p className="text-xs xl:text-sm text-slate-500 mb-3 xl:mb-4">Prossimi 30 giorni</p>
          <div className="space-y-2 xl:space-y-3 max-h-56 xl:max-h-72 overflow-y-auto scrollbar-thin" data-testid="expiring-list">
            {expiring.length === 0 && <p className="text-sm text-slate-400">Nessuna polizza in scadenza.</p>}
            {expiring.slice(0, 8).map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.client_name || "Cliente"}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
                    <span>{p.policy_number}</span>
                    <PolicyTypeBadge code={p.type} size="xs" testid={`dash-expiring-type-${p.id}`} />
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium">
                    {formatDate(p.effective_end_date || p.end_date)}
                    {p.effective_end_date && p.end_date && p.effective_end_date !== p.end_date && (
                      <span className="ml-1 text-[8px] font-bold px-1 py-px rounded bg-blue-100 text-blue-800 border border-blue-200 uppercase tracking-tight align-middle" title="Prossima rata da pagare">rata</span>
                    )}
                  </p>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 🆕 Dialog anteprima avviso scadenza (con opzione "non chiedermelo più") */}
      <EmailPreviewDialog
        open={!!expiryPreview}
        onOpenChange={(o) => { if (!o) setExpiryPreview(null); }}
        emailTypeKey="policy_expiry_notice"
        title="Anteprima avviso di scadenza polizza"
        fetchPreview={async () => {
          const { data } = await api.post(`/policies/${expiryPreview.policy.id}/send-expiry-notice?dry_run=true`);
          return data;
        }}
        onConfirmSend={async () => {
          const p = expiryPreview?.policy;
          setExpiryPreview(null);
          if (p) await performExpirySend(p);
        }}
      />

      {/* 🆕 28/07/2026 — Dialog "Avvisa" con scelta canale (Email / WhatsApp / Entrambi) */}
      <ExpiryNoticeDialog
        open={!!noticeDialog}
        policy={noticeDialog?.policy}
        client={noticeDialog?.client}
        onClose={() => setNoticeDialog(null)}
        onSent={() => loadAll()}
      />
    </div>
  );
}
